import React, { useState, useEffect } from "react";
import notify from "utils/notify";
import { Grid, Box, Typography, TextField, Autocomplete, CircularProgress, IconButton } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmsIcon from "@mui/icons-material/Sms";
import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";

import Modal from "../common/Modal";
import TextFieldGroup from "../common/TextFieldGroup";

import { intl } from "../../i18n/provider";
import ApiRequest from "../../actions/ApiRequest";

const ShareModal = ({
  shareModal,
  setShareModal,
  referalCode,
  contentMessage = null,
  meterNumber = null, // New prop for NEDCO mobile number storage
}) => {
  const [selectedShare, setSelectedShare] = useState(""); // 'whatsapp', 'sms', 'email'

  const [number, setNumber] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(contentMessage);
  const [shareLoading, setShareLoading] = useState(false);

  // State for saved mobile numbers
  const [savedMobiles, setSavedMobiles] = useState([]);
  const [loadingMobiles, setLoadingMobiles] = useState(false);

  // Fetch saved mobile numbers when modal opens and meterNumber is provided
  useEffect(() => {
    const fetchSavedMobiles = async () => {
      if (!shareModal || !meterNumber) {
        setSavedMobiles([]);
        return;
      }

      try {
        setLoadingMobiles(true);
        const response = await ApiRequest.get(
          `electric_co_get_mobiles?meter_number=${meterNumber}`
        );

        if (response?.data?.status === 'success' && response?.data?.mobiles) {
          setSavedMobiles(response.data.mobiles);
        } else {
          setSavedMobiles([]);
        }
      } catch (error) {
        console.log('Error fetching saved mobiles:', error.message);
        setSavedMobiles([]);
      } finally {
        setLoadingMobiles(false);
      }
    };

    fetchSavedMobiles();
  }, [shareModal, meterNumber]);

  // Save mobile number after successful share
  const saveMobileNumber = async (mobileToSave) => {
    if (!meterNumber || !mobileToSave) return;

    try {
      await ApiRequest.post(
        `electric_co_save_mobile?meter_number=${meterNumber}&mobile=${mobileToSave}`
      );
      console.log('Mobile number saved successfully');
    } catch (error) {
      console.log('Error saving mobile number:', error.message);
    }
  };

  // Delete mobile number
  const deleteMobileNumber = async (mobileToDelete) => {
    if (!meterNumber || !mobileToDelete) return;

    try {
      const response = await ApiRequest.post(
        `electric_co_delete_mobile?meter_number=${meterNumber}&mobile=${mobileToDelete}`
      );

      if (response?.data?.status === 'success') {
        // Remove from local state
        setSavedMobiles(prev => prev.filter(m => m !== mobileToDelete));
        notify.success("Number deleted");

        // Clear input if they had this number selected
        if (number === mobileToDelete) {
          setNumber("");
        }
      } else {
        notify.failure("Failed to delete number");
      }
    } catch (error) {
      console.log('Error deleting mobile number:', error.message);
      notify.failure("Failed to delete number");
    }
  };

  const shareProfile = async () => {
    setShareLoading(true);

    let messageToSend = "";
    if (contentMessage) {
      messageToSend = contentMessage;
    } else {
      messageToSend = `Voucher Code: ${referalCode || ''}`;
    }

    const encodedMsg = encodeURIComponent(messageToSend);
    try {
      await ApiRequest.post(
        `share?share_id=${selectedShare}&number=${number}&message=${encodedMsg}&email=${email}`
      );
      notify.success("Message Sent");

      // Save mobile number for future use (only for whatsapp/sms and when meterNumber is provided)
      if (meterNumber && (selectedShare === 'whatsapp' || selectedShare === 'sms') && number) {
        await saveMobileNumber(number);
      }
    } catch {
      notify.failure("Something went wrong!!, please try again");
    } finally {
      setShareLoading(false);
      setSelectedShare("");
      setNumber("");
      setMessage("");
      setEmail("");
    }
  };

  const handleShareSelect = (method) => {
    setSelectedShare(method);
    if (method === "sms" || method === "whatsapp") {
      setEmail("");
    } else {
      setNumber("");
    }
  };

  return (
    <Modal
      visible={shareModal}
      onClose={() => setShareModal(false)}
      title="share"
      width="600px"
      height="auto"
      onSuccess={async () => {
        await shareProfile();
        setShareModal(false);
      }}
      onSuccessDisable={!((selectedShare === 'email' ? email : number) && selectedShare)}
      onSuccessLoading={shareLoading}
    >
      <form className="phone-form">
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            {intl.formatMessage({ id: "Share Option" })} <span style={{ color: 'red' }}>*</span>
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Box
                onClick={() => handleShareSelect('whatsapp')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 2,
                  borderRadius: 2,
                  border: selectedShare === 'whatsapp' ? '2px solid #25D366' : '1px solid #e0e0e0',
                  bgcolor: selectedShare === 'whatsapp' ? 'rgba(37, 211, 102, 0.1)' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: 'rgba(37, 211, 102, 0.05)' }
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 40, color: '#25D366', mb: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: selectedShare === 'whatsapp' ? 'bold' : 'normal' }}>
                  WhatsApp
                </Typography>
              </Box>
            </Grid>

            <Grid item>
              <Box
                onClick={() => handleShareSelect('sms')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 2,
                  borderRadius: 2,
                  border: selectedShare === 'sms' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  bgcolor: selectedShare === 'sms' ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.05)' }
                }}
              >
                <SmsIcon sx={{ fontSize: 40, color: '#1976d2', mb: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: selectedShare === 'sms' ? 'bold' : 'normal' }}>
                  SMS
                </Typography>
              </Box>
            </Grid>

            <Grid item>
              <Box
                onClick={() => handleShareSelect('email')}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 2,
                  borderRadius: 2,
                  border: selectedShare === 'email' ? '2px solid #ea4335' : '1px solid #e0e0e0',
                  bgcolor: selectedShare === 'email' ? 'rgba(234, 67, 53, 0.1)' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: 'rgba(234, 67, 53, 0.05)' }
                }}
              >
                <EmailIcon sx={{ fontSize: 40, color: '#ea4335', mb: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: selectedShare === 'email' ? 'bold' : 'normal' }}>
                  Email
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {(selectedShare === "sms" || selectedShare === "whatsapp") && (
          meterNumber && savedMobiles.length > 0 ? (
            // Show Autocomplete with saved mobiles when meterNumber is provided
            <div className="form-group" style={{ width: '100%', maxWidth: '450px', margin: '0 auto' }}>
              <Autocomplete
                freeSolo
                options={savedMobiles}
                loading={loadingMobiles}
                value={number}
                onChange={(event, newValue) => {
                  setNumber(newValue || "");
                }}
                onInputChange={(event, newInputValue) => {
                  if (event) {
                    setNumber(newInputValue || "");
                  }
                }}
                sx={{ width: '100%' }}
                ListboxProps={{
                  style: {
                    maxHeight: '400px', // Fits about 15 numbers
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={intl.formatMessage({ id: "number" })}
                    placeholder={intl.formatMessage({ id: "number" })}
                    fullWidth
                    sx={{ mb: 2 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMobiles ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                      {option}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent selecting the option
                        deleteMobileNumber(option);
                      }}
                      sx={{
                        ml: 1,
                        color: '#ea4335',
                        '&:hover': { bgcolor: 'rgba(234, 67, 53, 0.1)' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </li>
                )}
              />
            </div>
          ) : (
            // Show regular text field when no saved mobiles
            <TextFieldGroup
              name="number"
              placeholder={intl.formatMessage({ id: "number" })}
              label={intl.formatMessage({ id: "number" })}
              value={number}
              type="text"
              onChange={(e) => setNumber(e.target.value)}
            />
          )
        )}
        {selectedShare === "email" && (
          <TextFieldGroup
            name="email"
            placeholder={intl.formatMessage({ id: "email" })}
            label={intl.formatMessage({ id: "email" })}
            value={email}
            type="text"
            onChange={(e) => setEmail(e.target.value)}
          />
        )}
        {!contentMessage && (
          <TextFieldGroup
            name="message"
            placeholder={intl.formatMessage({ id: "message" })}
            label={intl.formatMessage({ id: "message" })}
            value={message}
            type="text"
            onChange={(e) => setMessage(e.target.value)}
          />
        )}
      </form>
    </Modal>
  );
};

export default ShareModal;
